/**
 * calendarScopeStore — shared two-way switch for the dividend
 * calendar / income heatmap "include watchlist?" toggle.
 *
 * Round 92o++++++++N++++++++++ (Jun 2026).
 *
 * Tiny subscribable store (no Redux/Zustand) so the toggle on the
 * Dashboard 12-Month Heatmap card and the toggle on the standalone
 * Dividend Calendar page move in lockstep — flipping one flips the
 * other. Persists across reloads via localStorage so the user's
 * choice survives navigation + tab refreshes.
 *
 * Mode:
 *   'portfolio'  → only owned positions (lots with shares > 0)
 *   'both'       → portfolio + watchlist (legacy default)
 *
 * Backend translation: when mode === 'portfolio', any consumer
 * appends `&include_watchlist=false` to its /api/portfolio/* query.
 */

const STORAGE_KEY = 'dd_calendar_scope_v1';
const VALID = new Set(['portfolio', 'both']);

function _readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && VALID.has(raw)) return raw;
  } catch { /* SSR / privacy mode */ }
  return 'both';
}

let mode = _readStored();
const listeners = new Set();

export function getMode() {
  return mode;
}

/** True when consumers should request `include_watchlist=false`. */
export function isPortfolioOnly() {
  return mode === 'portfolio';
}

export function setMode(next) {
  if (!VALID.has(next) || next === mode) return;
  mode = next;
  try { localStorage.setItem(STORAGE_KEY, mode); } catch { /* noop */ }
  for (const l of listeners) {
    try { l(mode); } catch { /* noop */ }
  }
}

export function toggle() {
  setMode(mode === 'portfolio' ? 'both' : 'portfolio');
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
